/**
 * Theme registry — the ONE place you touch to add or remove a visual theme.
 *
 * To add a theme:
 *   1. Drop a self-contained `src/styles/themes/<id>.css` file that defines
 *      its tokens under `:root[data-theme='<id>']` (see glass.css for the
 *      pattern, including frosted-surface overrides via [data-surface] hooks).
 *   2. Add an `import` for it below.
 *   3. Add one entry to THEMES.
 *
 * To remove a theme: reverse those three steps. No other theme file and no
 * component needs to change — themes are fully isolated from one another.
 *
 * The string `id` is the contract: it is written to the `data-theme`
 * attribute on <html> and is the value stored in the backend `ui.theme`
 * config key.
 *
 * ⚠️ `text-brand` GOTCHA (read before adding a dark / low-contrast-brand theme):
 * a few components colour TEXT with the `--brand` token. If your theme's
 * `--brand` is a dark or near-black colour (common for dark themes that use a
 * black title bar), that text becomes illegible (dark-on-dark). You MUST add
 * per-theme overrides recolouring it to a legible accent:
 *   - active patient-record sub-tab:  `button[aria-current='page']`
 *     (set `color` + `border-bottom-color`)
 *   - active wizard step label:        `[aria-current='step'] + span`
 *     (set `color`)
 * See gray-dark.css / nebula-dark.css / high-contrast-dark.css for the pattern.
 * (Themes whose `--brand` is light enough to read as text — e.g. glass-dark,
 * high-contrast-light — don't need these.)
 */

// --- registered theme stylesheets (side-effect imports) ---
// spine-base holds the shared "Refined Spine" light palette (default + orange);
// the flavour files override only the hue. Import the base first.
import '../styles/themes/spine-base.css';
import '../styles/themes/default-light.css';
import '../styles/themes/orange-light.css';
// Accessibility themes (shown in the picker's "Accessibility" submenu).
import '../styles/themes/high-contrast-light.css';
import '../styles/themes/high-contrast-dark.css';
import '../styles/themes/colorblind-safe-light.css';
import '../styles/themes/inkwash-light.css';
import '../styles/themes/glass-light.css';
import '../styles/themes/glass-dark.css';
import '../styles/themes/gray-dark.css';
import '../styles/themes/nebula-dark.css';
// stratum-dark and bokeh-dark stay imported (still valid, applyable via URL /
// config) but are hidden from the picker — see `hidden` in their THEMES entry.
import '../styles/themes/stratum-dark.css';
import '../styles/themes/bokeh-dark.css';
// win98-base.css holds the shared "98" styling; the flavour files set only the
// desktop colour. Import the base first so flavour overrides win cleanly.
import '../styles/themes/win98-base.css';
import '../styles/themes/win98-teal-light.css';
import '../styles/themes/win98-gray-light.css';

export interface ThemeMeta {
  /** Stable id — value of `data-theme` and of the `ui.theme` config key. */
  readonly id: string;
  /** Human-readable name for a theme picker. */
  readonly label: string;
  /** Light or dark — groups the theme picker. */
  readonly mode: 'light' | 'dark';
  /** One-line description for admin UI. */
  readonly description: string;
  /**
   * When true, the theme stays registered (a valid id, applyable via
   * `?theme=` / backend config) but is omitted from the in-app theme picker.
   */
  readonly hidden?: boolean;
  /**
   * Picker grouping. 'official' themes sit in the top (unlabeled) section;
   * everything else falls into the bottom "Unofficial" section. Defaults to
   * unofficial when omitted.
   */
  readonly category?: 'official' | 'unofficial';
  /**
   * When true (and category is 'official'), the theme is shown inside the
   * "Accessibility" submenu rather than the top-level official list.
   */
  readonly accessibility?: boolean;
}

// Order matters for the picker: light themes first (default-light topmost),
// then dark. See ThemeMenu, which groups by `mode` with a divider.
export const THEMES = [
  {
    id: 'default-light',
    label: 'Default (Light)',
    mode: 'light',
    description: 'The standard PhenoTips light theme.',
    category: 'official',
  },
  {
    id: 'orange-light',
    label: 'Orange (Light)',
    mode: 'light',
    description: 'The default theme with the blue hue rotated to orange.',
    category: 'official',
  },
  {
    id: 'high-contrast-light',
    label: 'High Contrast (Light)',
    mode: 'light',
    description:
      'Maximum-legibility light theme (WCAG AAA): black on white, solid borders, no glass.',
    category: 'official',
    accessibility: true,
  },
  {
    id: 'high-contrast-dark',
    label: 'High Contrast (Dark)',
    mode: 'dark',
    description:
      'Maximum-legibility dark theme (WCAG AAA): white on black, yellow/cyan accents, solid borders.',
    category: 'official',
    accessibility: true,
  },
  {
    id: 'colorblind-safe-light',
    label: 'Colorblind-Safe (Light)',
    mode: 'light',
    description:
      'Light theme using the Okabe-Ito palette so status colours stay distinguishable for red-green and blue-yellow deficiencies.',
    category: 'official',
    accessibility: true,
  },
  {
    id: 'inkwash-light',
    label: 'Inkwash (Light)',
    mode: 'light',
    description:
      'Muted teal watercolour on aged cream paper; panels bleed unevenly past their edges.',
  },
  {
    id: 'glass-light',
    label: 'Glass (Light)',
    mode: 'light',
    description:
      'Light frosted, semi-transparent panels over a soft gradient backdrop.',
  },
  {
    id: 'win98-teal-light',
    label: '98 Teal (Light)',
    mode: 'light',
    description: 'Windows 98 — silver-gray 3D bevels, navy title bar, teal desktop.',
    hidden: true,
  },
  {
    id: 'win98-gray-light',
    label: '98 Gray (Light)',
    mode: 'light',
    description: 'Windows 98 — silver-gray 3D bevels, navy title bar, gray desktop.',
  },
  {
    id: 'glass-dark',
    label: 'Glass (Dark)',
    mode: 'dark',
    description:
      'Dark, sci-fi-style frosted panels over a deep space backdrop.',
  },
  {
    id: 'gray-dark',
    label: 'Gray (Dark)',
    mode: 'dark',
    description: 'Flat dark theme in the JetBrains One Dark style.',
  },
  {
    id: 'nebula-dark',
    label: 'Nebula (Dark)',
    mode: 'dark',
    description:
      'Brushed dark-grey shell panels with thick translucent glass borders over a teal-and-gold nebula.',
  },
  {
    id: 'stratum-dark',
    label: 'Stratum (Dark)',
    mode: 'dark',
    description:
      'Stacked horizontal blue strata with brushed sheen and an electric cyan highlight.',
    hidden: true,
  },
  {
    id: 'bokeh-dark',
    label: 'Bokeh (Dark)',
    mode: 'dark',
    description:
      'Soft glowing circles drifting warm-to-cool across near-black; round, amber-accented panels.',
    hidden: true,
  },
] as const satisfies readonly ThemeMeta[];

export type ThemeId = (typeof THEMES)[number]['id'];

/** The theme used when no valid `ui.theme` value is available. */
export const DEFAULT_THEME_ID: ThemeId = 'default-light';

/** Narrow an arbitrary string (e.g. a config value) to a known ThemeId. */
export function isThemeId(value: string | null | undefined): value is ThemeId {
  return (
    value != null && THEMES.some((theme) => theme.id === value)
  );
}

/** Coerce any input to a valid ThemeId, falling back to the default. */
export function toThemeId(value: string | null | undefined): ThemeId {
  return isThemeId(value) ? value : DEFAULT_THEME_ID;
}
